'use client'

import { type ReactNode, useRef } from 'react'
import { cn } from '@/lib/utils'

interface GlowCardProps {
  children: ReactNode
  className?: string
  glowColor?: 'blue' | 'purple' | 'green' | 'orange' | 'red' | 'teal'
  size?: 'sm' | 'md' | 'lg'
  width?: number | string
  height?: number | string
  customSize?: boolean
}

const glowColorMap = {
  blue: 'before:bg-blue-500',
  purple: 'before:bg-purple-500',
  green: 'before:bg-green-500',
  red: 'before:bg-red-500',
  orange: 'before:bg-gold before:opacity-20', // Use gold for orange
  teal: 'before:bg-teal-500',
}

const sizeMap = {
  sm: 'w-48 h-64',
  md: 'w-64 h-80',
  lg: 'w-80 h-96',
}

export function GlowCard({
  children,
  className = '',
  glowColor = 'blue',
  size = 'md',
  customSize = false,
}: GlowCardProps) {
  const cardRef = useRef<HTMLDivElement>(null)

  const getSizeClasses = () => {
    if (customSize) {
      return ''
    }
    return sizeMap[size]
  }

  return (
    <div
      ref={cardRef}
      className={cn(
        'relative rounded-2xl border border-white/10 bg-[#212121] p-px',
        'transition-all duration-300 ease-in-out',
        'hover:border-gold/50 hover:bg-[#2a2a2a] hover:shadow-[0_0_20px_rgba(255,215,0,0.1)]', // Gold hover effect
        getSizeClasses(),
        !customSize ? 'aspect-[3/4]' : '',
        className,
      )}
    >
      {/* Static Glow Pseudo-element */}
      <div
        className={cn(
          'absolute inset-0 -z-10 overflow-hidden rounded-2xl',
          'before:absolute before:inset-0 before:opacity-10 before:blur-2xl',
          'before:will-change-transform', // Performance optimization
          glowColorMap[glowColor],
        )}
      />
      <div className="relative z-10 h-full">{children}</div>
    </div>
  )
}
